import { FormInstance } from 'antd';
import { NamePath } from 'antd/lib/form/interface';
import ContentStore from '../stores/ContentStore';
import { ProductType } from '../swagger/api';
import CMSStore, { Locale } from '../stores/content-stores/CMSStore';

const validateOnlyNumbersAndExactEight = (form: FormInstance) => {
  if (!form.getFieldValue('phoneNumber').match('^[0-9]{8}$')) {
    return Promise.reject(new Error(''));
  }
  return Promise.resolve();
};

export const validateCommonLicensePlate = (licensePlateInput: string): boolean =>
  /^[\p{L}0-9]+$/gu.test(licensePlateInput);

export const validateDanishLicensePlate = (licensePlateInput: string): boolean =>
  /^([\p{L}]{2})(\d{2})(\d{3})$/gu.test(licensePlateInput);

export const validateSwedishLicensePlate = (licensePlateInput: string): boolean =>
  /^[\p{L}]{3}[0-9]{2}[\p{L}0-9]{1}$/gu.test(licensePlateInput);

export const validateGermanOrAustrianLicensePlate = (licensePlateInput: string): boolean =>
  /^[\p{L}0-9]+$/gu.test(licensePlateInput);

export const validateLicensePlate = (isoCode: string, licensePlate: string): boolean => {
  switch (isoCode) {
    case 'DK':
      return validateDanishLicensePlate(licensePlate);
    case 'SE':
      return validateSwedishLicensePlate(licensePlate);
    case 'DE':
    case 'AT':
      return validateGermanOrAustrianLicensePlate(licensePlate);
    default:
      return validateCommonLicensePlate(licensePlate);
  }
};

export const validateLicensePlateInput = (
  cmsStore: CMSStore,
  isoCode: string,
  licensePlate: string,
  errorMessage?: string
): string | null => {
  let validationFunction: (value: string) => boolean;
  let validationError: string | undefined;
  switch (isoCode) {
    case 'DK':
      validationFunction = validateDanishLicensePlate;
      validationError = cmsStore.getContent('LicensePlateIsNotFromDenmark')?.content;
      break;
    case 'SE':
      validationFunction = validateSwedishLicensePlate;
      validationError = cmsStore.getContent('LicensePlateIsNotFromSweden')?.content;
      break;
    case 'DE':
    case 'AT':
      validationFunction = validateGermanOrAustrianLicensePlate;
      validationError = cmsStore.getContent('LicensePlateIsNotFromGermanyOrAustria')?.content;
      break;
    default:
      validationFunction = validateCommonLicensePlate;
      validationError = cmsStore.getContent('LicensePlateIsNotCorrect')?.content;
      break;
  }

  const cmsLocale = cmsStore.getCMSLocale();
  const defaultErrorMessage =
    cmsLocale === Locale.Danish ? 'Validering af feltet mislykkedes' : 'Field validation failed';

  if (!isoCode || !licensePlate || !validationFunction(licensePlate)) {
    return validationError || errorMessage || defaultErrorMessage;
  }

  return null;
};

export const validateOnlyNumbers = (value: string) => {
  if (value !== '' && !value.match('^[0-9]+$')) {
    return Promise.reject(new Error(''));
  }
  return Promise.resolve();
};

export const isValidEmailFormat = (value: string) => {
  const emailRegex = '^[a-zA-Z0-9._%+-]+@([a-zA-Z0-9-]+\\.)+[a-zA-Z]{2,}$';
  return value.match(emailRegex);
};

export const validateIsEmailValid = async (email: string) => {
  if (isValidEmailFormat(email)) {
    return Promise.resolve();
  }
  return Promise.reject();
};

export const validateDanishPhoneNumber = async (
  _: any,
  value: any,
  form: FormInstance
  // eslint-disable-next-line consistent-return
) => {
  if (value !== '' && form.getFieldValue('phonePrefix') === '+45') {
    return validateOnlyNumbersAndExactEight(form);
  }
};

export const validateForeignPhoneNumber = async (
  _: any,
  value: any,
  form: FormInstance
  // eslint-disable-next-line consistent-return
) => {
  if (value !== '' && form.getFieldValue('phonePrefix') !== '+45') {
    return validateOnlyNumbers(form.getFieldValue('phoneNumber'));
  }
};

export const getIsChassisNumberMandatory = (
  contentStore: ContentStore,
  form: FormInstance,
  countryIdFormItemName: NamePath | string,
  productType: ProductType,
  key?: number
) => {
  const isApprovedCountry = contentStore.genericContentStore.getApprovedCountriesIsoCodes.includes(
    form.getFieldValue(countryIdFormItemName)
  );

  // chassisNumber is mandatory when documentation is uploaded (for non-approved countries and not OneTimeANPR) and when ordering an eTicket with vehicle with an approved country
  let isMandatory = false;
  if (!isApprovedCountry && productType !== ProductType.OneTimeANPR) {
    if (key !== undefined) {
      isMandatory = !!form.getFieldValue([key, 'encodedDocumentPdf']);
    } else {
      isMandatory = !!(
        form.getFieldValue('encodedDocumentPdf') || form.getFieldValue('vehicleDocumentId')
      );
    }
  }

  if (isApprovedCountry && productType === ProductType.OneTimeANPR) {
    isMandatory = true;
  }
  return isMandatory;
};

export const validateChassisNumber = (
  contentStore: ContentStore,
  form: FormInstance,
  countryIdFormItemName: NamePath | string,
  requiredFieldErrorMessage: string,
  productType: ProductType,
  key?: number
) => {
  const vinFormItemName = key || key === 0 ? [key, 'vin'] : 'vin';
  if (
    getIsChassisNumberMandatory(contentStore, form, countryIdFormItemName, productType, key) &&
    (!form.getFieldValue(vinFormItemName) || !form.getFieldValue(vinFormItemName).trim())
  )
    return Promise.reject(new Error(requiredFieldErrorMessage));
  return Promise.resolve();
};
